import React, { useState } from "react";
import styled from "styled-components";
import { useImmer } from "use-immer";

import { gql } from "@examspark/api";
import { IssueType, Resource, ResourcePriorityEnum } from "@examspark/types";

import Button from "../common/Button";
import Modal, { ModalProps } from "../modal/Modal";

const PrimaryButton = styled(Button)`
  background-color: #006699;
  color: white;
  border: none;
  cursor: pointer;
  text-transform: uppercase;
  font-weight: bold;

  &:hover {
    background-color: #0170a7;
  }
  &:disabled {
    cursor: not-allowed;
  }
`;

const Form = styled.div`
  padding: 1.5rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;
const FormItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;
const FormItemLabel = styled.div`
  color: #777;
`;
const FormItemField = styled.div``;

const FormOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;
const FormOption = styled.div`
  background-color: #f7f7f7;
  display: flex;
  gap: 1rem;
  padding: 1rem;
  align-items: center;
  cursor: pointer;
`;
const FormOptionInput = styled.div``;
const FormOptionLabel = styled.div`
  flex: 1;
`;

const FormTextInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: solid 1px #ddd;
  padding: 0.7rem;
  border-radius: 0.5rem;
  margin: 0.2rem 0;
  transition: border-color 0.2s;

  &:focus {
    margin: 0.2rem 0;
    padding: 0.6rem;
    border: solid 2px #006699;
  }
`;
const FormTextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  height: 10rem;
  border: solid 1px #ddd;
  padding: 0.7rem;
  border-radius: 0.5rem;
  margin: 0.2rem 0;
  transition: border-color 0.2s;

  &:focus {
    margin: 0.2rem 0;
    padding: 0.6rem;
    border: solid 2px #006699;
  }
`;

const Complete = styled.div`
  display: flex;
  padding: 8rem;
  align-items: center;
  justify-content: center;
  gap: 2rem;
`;
const CompleteIcon = styled.div``;
const CompleteText = styled.div`
  h2,
  h3 {
    margin: 0.5rem 0;
    font-weight: normal;
  }
  h3 {
    color: #777;
  }
`;

interface Props extends ModalProps {
  resource?: Resource;
  reporterUserId: string;
}

const ContentFeedbackModal: React.FC<Props> = (props) => {
  const [view, setView] = useState("form");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, updateForm] = useImmer<{
    issueTypes: string[];
    title: string;
    details: string;
  }>({
    issueTypes: [],
    details: "",
    title: "",
  });

  const resetForm = () => {
    setView("form");
    updateForm((draft) => ({
      issueTypes: [],
      details: "",
      title: "",
    }));
  };

  const createFeedback = async () => {
    if (!props.resource) return;
    if (!form.title.trim().length) {
      alert("Enter a title");
      return;
    }

    setIsSubmitting(true);
    console.log("creating feedback", form, props.resource);

    await Promise.all(
      form.issueTypes.map(async (type) => {
        if (!props.resource) return;
        return await gql.operations.issue.createIssue({
          id: props.resource.id,
          sk: props.resource.sk,
          workspaceId: props.resource.workspaceId,
          name: form.title,
          description: form.details,
          maturity: "TODO",
          type,
          priority: ResourcePriorityEnum.Medium,
          creatorId: props.reporterUserId,
        });
      }),
    );

    setView("complete");
    setIsSubmitting(false);
  };

  if (!props.resource) return null;

  return (
    <Modal
      title="Send Item Feedback"
      buttons={
        view === "form" && (
          <PrimaryButton
            disabled={isSubmitting}
            onClick={() => createFeedback()}
          >
            Send Feedback
          </PrimaryButton>
        )
      }
      onAfterClose={() => resetForm()}
      {...props}
    >
      {view === "form" && (
        <Form>
          <FormItem>
            <FormItemLabel>
              Tell us what is wrong with the content.
            </FormItemLabel>
            <FormItemField>
              <FormOptions>
                {[
                  {
                    value: IssueType.Preview,
                    label: (
                      <>
                        <strong>Difficult to use</strong> on my computer or
                        other device.
                      </>
                    ),
                  },
                  {
                    value: IssueType.Scoring,
                    label: (
                      <>
                        <strong>Scoring</strong> for this item is incorrect or
                        incomplete.
                      </>
                    ),
                  },
                  {
                    value: IssueType.SpellingGrammar,
                    label: (
                      <>
                        <strong>Spelling</strong> or <strong>grammar</strong>{" "}
                        issues.
                      </>
                    ),
                  },
                  {
                    value: IssueType.SubjectMatter,
                    label: (
                      <>
                        This material is <strong>factually inaccurate</strong>.
                      </>
                    ),
                  },
                  {
                    value: IssueType.Alignment,
                    label: (
                      <>
                        <strong>Learning standards</strong> are missing or
                        incorrect.
                      </>
                    ),
                  },
                  {
                    value: IssueType.Bias,
                    label: (
                      <>
                        This material contains <strong>cultural bias</strong>.
                      </>
                    ),
                  },
                ].map((opt, idx) => {
                  return (
                    <FormOption
                      key={idx}
                      onClick={() => {
                        updateForm((draft) => {
                          const findIndex = draft.issueTypes.findIndex(
                            (type) => type === opt.value,
                          );
                          if (findIndex !== -1) {
                            draft.issueTypes.splice(findIndex, 1);
                          } else {
                            draft.issueTypes.push(opt.value);
                          }
                        });
                      }}
                    >
                      <FormOptionInput>
                        <input
                          type="checkbox"
                          onChange={() => {
                            //
                          }}
                          checked={form.issueTypes.includes(opt.value)}
                        />
                      </FormOptionInput>
                      <FormOptionLabel>{opt.label}</FormOptionLabel>
                    </FormOption>
                  );
                })}
              </FormOptions>
            </FormItemField>
          </FormItem>
          <FormItem>
            <FormItemLabel>
              Provide a brief description of the problem.
            </FormItemLabel>
            <FormItemField>
              <FormTextInput
                value={form.title}
                onChange={(e) => {
                  updateForm((draft) => {
                    draft.title = e.target.value;
                  });
                }}
              />
            </FormItemField>
          </FormItem>
          <FormItem>
            <FormItemLabel>
              Can you provide any supporting details?
            </FormItemLabel>
            <FormItemField>
              <FormTextArea
                value={form.details}
                onChange={(e) => {
                  updateForm((draft) => {
                    draft.details = e.target.value;
                  });
                }}
              />
            </FormItemField>
          </FormItem>
        </Form>
      )}
      {view === "complete" && (
        <Complete>
          <CompleteIcon>
            <svg width="48" viewBox="0 0 512 512" fill="#2cb02c">
              <g>
                <g>
                  <path d="M383.841,171.838c-7.881-8.31-21.02-8.676-29.343-0.775L221.987,296.732l-63.204-64.893    c-8.005-8.213-21.13-8.393-29.35-0.387c-8.213,7.998-8.386,21.137-0.388,29.35l77.492,79.561    c4.061,4.172,9.458,6.275,14.869,6.275c5.134,0,10.268-1.896,14.288-5.694l147.373-139.762    C391.383,193.294,391.735,180.155,383.841,171.838z"></path>
                </g>
              </g>
              <g>
                <g>
                  <path d="M256,0C114.84,0,0,114.84,0,256s114.84,256,256,256s256-114.84,256-256S397.16,0,256,0z M256,470.487    c-118.265,0-214.487-96.214-214.487-214.487c0-118.265,96.221-214.487,214.487-214.487c118.272,0,214.487,96.221,214.487,214.487    C470.487,374.272,374.272,470.487,256,470.487z"></path>
                </g>
              </g>
            </svg>
          </CompleteIcon>
          <CompleteText>
            <h2>Thank you for your feedback!</h2>
            <h3>You will be notified by email when this issue is resolved.</h3>
          </CompleteText>
        </Complete>
      )}
    </Modal>
  );
};

export default ContentFeedbackModal;
